import { useId } from "react";

import { Alert, Label, Small } from "@fronterahealth/frontera-ui-components";

import { InfoTooltip } from "@components/InfoTooltip";
import { TooltipSide } from "@components/constants";
import { EhrSyncMode } from "@pages/Organizations/constants";
import { EHR_SYNC_COPY, resolveEhrSyncEnabled } from "@pages/Organizations/ehrSync";

interface EhrSyncFieldProps {
  mode: EhrSyncMode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Shown (and announced) when leaving Passage Health turned the box off. */
  resetNotice?: string | null;
}

/**
 * EHR sync checkbox for the Create new organization form. Its state follows
 * the EHR system (see `getEhrSyncMode`).
 */
export const EhrSyncField: React.FC<EhrSyncFieldProps> = ({ mode, checked, onChange, resetNotice }) => {
  const checkboxId = useId();
  const helpId = useId();
  const warningId = useId();
  const noticeId = useId();

  const isAlwaysOn = mode === EhrSyncMode.ALWAYS_ON;
  const isDisabled = mode === EhrSyncMode.UNSELECTED;
  const isChecked = resolveEhrSyncEnabled(mode, checked);
  // Whenever the org will be created with sync on, Passage included.
  const showWarning = isChecked;
  const help =
    mode === EhrSyncMode.EDITABLE
      ? `${EHR_SYNC_COPY.help[EhrSyncMode.EDITABLE]} ${EHR_SYNC_COPY.changeLaterHint}`
      : EHR_SYNC_COPY.help[mode];

  return (
    <fieldset className="min-w-0">
      <legend className="flex items-center gap-2">
        <Label displayType="normal" size="small" colorType="secondary">
          {EHR_SYNC_COPY.legend}
        </Label>
        {/* Opens upward: below, the popup would sit over the checkbox and
            catch the click meant for it. */}
        <InfoTooltip text={EHR_SYNC_COPY.tooltip} label={EHR_SYNC_COPY.tooltipLabel} side={TooltipSide.TOP} />
      </legend>

      <div className="mt-2 flex min-h-9 items-center gap-2">
        {/* Passage stays focusable (aria-disabled, not disabled) so screen
            readers still reach its "always on" description. */}
        <input
          id={checkboxId}
          type="checkbox"
          checked={isChecked}
          disabled={isDisabled}
          aria-disabled={isAlwaysOn || undefined}
          aria-describedby={[helpId, resetNotice ? noticeId : null, showWarning ? warningId : null]
            .filter(Boolean)
            .join(" ")}
          onChange={(event) => {
            if (mode === EhrSyncMode.EDITABLE) onChange(event.target.checked);
          }}
          className="h-4 w-4 rounded border-border-medium text-rio-600 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        />
        <label
          htmlFor={checkboxId}
          className={`text-sm ${mode === EhrSyncMode.EDITABLE ? "cursor-pointer text-text-primary" : "cursor-not-allowed text-text-tertiary"}`}
        >
          {EHR_SYNC_COPY.checkbox}
        </label>
      </div>

      <Small id={helpId} className="mt-1" colorType="secondary">
        {help}
      </Small>

      {/* Always rendered and never display:none: a live region has to be in
          the accessibility tree before its text changes for screen readers
          to announce it. Empty, it takes no space. */}
      <p id={noticeId} aria-live="polite" className={`text-sm text-text-secondary ${resetNotice ? "mt-2" : ""}`}>
        {resetNotice}
      </p>

      {showWarning && (
        <div id={warningId} className="mt-2">
          <Alert title="" appearance="warn" text={<p className="-mt-2">{EHR_SYNC_COPY.createWarning}</p>} />
        </div>
      )}
    </fieldset>
  );
};
